'use client';

import { useState } from 'react';

import { Loader2, Plus } from 'lucide-react';

import { useAuthModal } from '~/components/auth/auth-modal-context';
import { CheckoutOverlay } from '~/components/billing/checkout-overlay';
import { useDisplayCurrency } from '~/components/currency-provider';
import { UPGRADE_DISCOUNT_PERCENT } from '~/config/monetization.config';

const MONTHLY_PLAN_ID = 'pass-monthly';

/**
 * "UNLOCK MORE" premium tile injected into the character grids (home +
 * catalog). Design: the duo artwork as the card, the title on top, the price
 * panel (SAVE badge, gold struck-through old price) and CTA at the bottom. Clicking starts
 * the monthly checkout directly (auth modal for signed-out users).
 */
export function UpgradePromoCard() {
  const { symbol } = useDisplayCurrency();
  const { openAuthModal } = useAuthModal();
  const [loading, setLoading] = useState(false);
  const [checkoutToken, setCheckoutToken] = useState<string | null>(null);
  const [hovered, setHovered] = useState(false);

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/billing/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planId: MONTHLY_PLAN_ID }),
      });

      // Only 401 means "sign in needed"; everything else we send to /pricing.
      if (res.status === 401) {
        openAuthModal('sign-up', '/pricing');
        return;
      }

      const data = (await res.json().catch(() => ({}))) as {
        checkoutToken?: string;
      };

      if (data.checkoutToken) {
        setCheckoutToken(data.checkoutToken);
      } else {
        window.location.href = '/pricing';
      }
    } catch {
      window.location.href = '/pricing';
    } finally {
      setLoading(false);
    }
  };

  // Per-element "pop" on hover. Compounds with the card's own scale below.
  const pop = (scale: number) => ({
    transition: 'transform 0.4s ease',
    transform: hovered ? `scale(${scale})` : 'scale(1)',
  });

  return (
    <>
      <button
        type="button"
        onClick={handleUpgrade}
        disabled={loading}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        aria-label={`Unlock more: save ${UPGRADE_DISCOUNT_PERCENT}%, ${symbol}5.99 per month`}
        className="relative block w-full overflow-hidden rounded-3xl text-center disabled:cursor-wait"
        style={{
          containerType: 'inline-size',
          aspectRatio: '324 / 520',
          background:
            "url('/images/backgrounds/unlock-more-card.webp') center 35% / cover no-repeat, #2a0d22",
          border: '1px solid rgba(236,72,153,0.55)',
          transition:
            'transform 0.4s ease, filter 0.4s ease, box-shadow 0.4s ease',
          transform: hovered ? 'scale(1.02)' : 'scale(1)',
          filter: hovered ? 'brightness(1.08)' : 'brightness(1)',
          boxShadow: hovered
            ? '0 0 34px rgba(236,72,153,0.7), 0 0 85px rgba(236,72,153,0.55)'
            : '0 0 10px rgba(236,72,153,0.22)',
        }}
      >
        {/* Scrims: top for the title, bottom for the price and CTA */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0"
          style={{
            height: '30%',
            background:
              'linear-gradient(to bottom, rgba(30,6,24,0.7) 0%, transparent 100%)',
          }}
        />
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0"
          style={{
            height: '26%',
            background:
              'linear-gradient(to top, rgba(30,6,24,0.85) 0%, rgba(30,6,24,0.35) 60%, transparent 100%)',
          }}
        />

        <span
          className="absolute inset-x-0 top-0 flex flex-col items-center italic"
          style={{
            paddingTop: '6cqw',
            fontWeight: 900,
            lineHeight: 0.95,
            ...pop(1.04),
          }}
        >
          <span
            className="text-white"
            style={{
              fontSize: '17cqw',
              filter: 'drop-shadow(0 0 14px rgba(236,72,153,0.55))',
            }}
          >
            UNLOCK
          </span>
          <span
            style={{
              fontSize: '17cqw',
              background:
                'linear-gradient(180deg,#ff9fd6 0%,#f472b6 45%,#ec4899 100%)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
              filter: 'drop-shadow(0 0 16px rgba(236,72,153,0.7))',
            }}
          >
            MORE
          </span>
        </span>

        <span
          className="absolute inset-x-0 bottom-0 flex flex-col items-center"
          style={{ padding: '0 7cqw 5cqw', gap: '3cqw' }}
        >
          {/* Price panel with the SAVE badge on its top-left edge */}
          <span
            className="relative flex w-full items-baseline justify-center"
            style={{
              gap: '2.6cqw',
              padding: '4.4cqw 3.6cqw 2.8cqw',
              borderRadius: '4cqw',
              background: 'rgba(28,8,24,0.42)',
              border: '1px solid rgba(255,105,180,0.75)',
              boxShadow:
                '0 0 18px rgba(236,72,153,0.55), inset 0 0 14px rgba(236,72,153,0.18)',
              ...pop(1.04),
            }}
          >
            <span
              className="absolute font-extrabold text-white"
              style={{
                left: '4cqw',
                top: 0,
                transform: 'translateY(-55%)',
                padding: '1.4cqw 3.6cqw',
                fontSize: '4.4cqw',
                borderRadius: '999px',
                background: 'linear-gradient(180deg,#ff3fb4,#e11d8f)',
                boxShadow: '0 0 14px rgba(255,46,170,0.7)',
              }}
            >
              SAVE {UPGRADE_DISCOUNT_PERCENT}%
            </span>
            <span
              className="font-bold"
              style={{
                fontSize: '6.2cqw',
                color: '#f5c542',
                textDecoration: 'line-through',
                textDecorationColor: '#f5c542',
                textDecorationThickness: '0.6cqw',
              }}
            >
              {symbol}12.99
            </span>
            <span
              style={{
                fontSize: '12cqw',
                fontWeight: 900,
                lineHeight: 1,
                background: 'linear-gradient(180deg,#ff9fd6,#ec4899)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
                filter: 'drop-shadow(0 0 14px rgba(236,72,153,0.6))',
              }}
            >
              {symbol}5.99
            </span>
            <span className="text-white/85" style={{ fontSize: '5cqw' }}>
              /mo
            </span>
          </span>

          {/* CTA */}
          <span
            className="relative inline-flex w-full items-center justify-center overflow-hidden text-white"
            style={{
              padding: '3.4cqw 4cqw',
              fontSize: '5.6cqw',
              fontWeight: 800,
              borderRadius: '999px',
              background: 'linear-gradient(180deg,#ff3fb4 0%,#ec1c8f 100%)',
              border: '1px solid rgba(255,182,224,0.7)',
              boxShadow:
                '0 0 26px rgba(255,46,170,0.75), 0 0 60px rgba(236,72,153,0.45)',
              ...pop(1.05),
            }}
          >
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  'linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.5) 50%, transparent 65%)',
                transition: 'transform 0.7s ease-out',
                transform: hovered ? 'translateX(100%)' : 'translateX(-100%)',
              }}
            />
            <span
              className="relative z-10 inline-flex items-center"
              style={{ gap: '2.4cqw' }}
            >
              {loading ? (
                <Loader2
                  className="animate-spin"
                  style={{ width: '6.2cqw', height: '6.2cqw' }}
                />
              ) : (
                <>
                  UPGRADE NOW
                  <Plus
                    style={{ width: '6.4cqw', height: '6.4cqw' }}
                    strokeWidth={2.6}
                  />
                </>
              )}
            </span>
          </span>
        </span>
      </button>

      <CheckoutOverlay
        checkoutToken={checkoutToken}
        hideBack
        onBack={() => setCheckoutToken(null)}
        onComplete={() => setCheckoutToken(null)}
      />
    </>
  );
}
